import { useEffect, useState, useCallback } from "react";
import {
  Loader2, Smartphone, Phone, Mail, Star, Image as ImageIcon, X,
  CheckCircle2, XCircle, Send, Archive, Sparkles, MessageCircle, Clock, Package, PackageCheck, Trophy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  // Phase-1 statuses (used by service layer)
  new:                { label: "Neu",              cls: "bg-amber-500/15 text-amber-300 border-amber-500/30",       icon: Sparkles },
  under_review:       { label: "In Prüfung",       cls: "bg-blue-500/15 text-blue-300 border-blue-500/30",          icon: Clock },
  estimate_generated: { label: "KI-Schätzung",     cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",          icon: Sparkles },
  offer_draft:        { label: "Entwurf",          cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",          icon: Clock },
  offer_sent:         { label: "Angebot gesendet", cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",    icon: Send },
  customer_accepted:  { label: "Kunde angenommen", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
  customer_declined:  { label: "Kunde abgelehnt",  cls: "bg-red-500/15 text-red-300 border-red-500/30",             icon: XCircle },
  expired:            { label: "Abgelaufen",       cls: "bg-orange-500/15 text-orange-300 border-orange-500/30",    icon: Clock },
  device_received:    { label: "Gerät erhalten",   cls: "bg-teal-500/15 text-teal-300 border-teal-500/30",          icon: Package },
  device_checked:     { label: "Gerät geprüft",    cls: "bg-teal-500/15 text-teal-300 border-teal-500/30",          icon: PackageCheck },
  completed:          { label: "Abgeschlossen",    cls: "bg-emerald-600/20 text-emerald-200 border-emerald-500/40", icon: Trophy },
  cancelled:          { label: "Storniert",        cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",          icon: Archive },
  // Backward-compat with old records
  offered:            { label: "Angeboten",        cls: "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",    icon: Send },
  accepted:           { label: "Akzeptiert (alt)", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: CheckCircle2 },
  rejected:           { label: "Abgelehnt (alt)",  cls: "bg-red-500/15 text-red-300 border-red-500/30",             icon: XCircle },
  closed:             { label: "Geschlossen (alt)",cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",          icon: Archive },
};

const FILTER_STATUSES = ["all", "new", "offer_sent", "customer_accepted", "customer_declined", "completed", "expired"];

const CONDITION_LABEL = {
  1: "Defekt",
  2: "Gebraucht",
  3: "Normal",
  4: "Sehr gut",
  5: "Wie neu",
};

export default function AdminBuyback() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const r = await api.get("/admin/buyback", { params });
      setItems(r.data?.items || []);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const countByStatus = items.reduce((acc, i) => ({ ...acc, [i.status]: (acc[i.status] || 0) + 1 }), {});

  return (
    <div className="space-y-6" data-testid="admin-buyback-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Handy-Ankauf</h1>
          <p className="text-sm text-zinc-500 mt-1">KI-geschätzte Ankaufsanfragen — verbindliches Angebot setzen.</p>
        </div>
        <div className="flex gap-2 text-xs">
          {FILTER_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 h-9 rounded-full transition-all whitespace-nowrap ${
                filter === s
                  ? "bg-[#DC1928] text-white"
                  : "bg-[#141417] border border-white/10 text-zinc-400 hover:text-white"
              }`}
              data-testid={`buyback-filter-${s}`}
            >
              {s === "all" ? "Alle" : STATUS_META[s]?.label}
              {countByStatus[s] > 0 && <span className="ml-1.5 opacity-70">·{countByStatus[s]}</span>}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-20 text-center text-zinc-500 bg-[#141417] rounded-2xl border border-white/5" data-testid="buyback-empty">
          <Sparkles className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
          <div className="font-display text-base">Keine Ankaufsanfragen vorhanden.</div>
          <p className="text-xs mt-1">Sobald jemand über /ankauf etwas anbietet, erscheint es hier.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="buyback-grid">
          {items.map((it) => (
            <BuybackCard key={it.id} item={it} onClick={() => setSelected(it)} />
          ))}
        </div>
      )}

      <BuybackDetailDialog
        item={selected}
        onClose={() => setSelected(null)}
        onUpdated={() => { load(); setSelected(null); }}
      />
    </div>
  );
}

function BuybackCard({ item, onClick }) {
  const meta = STATUS_META[item.status] || STATUS_META.new;
  const ai = item.ai_estimate || {};
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left p-4 rounded-2xl bg-[#141417] border border-white/5 hover:border-white/15 transition-colors w-full"
      data-testid={`buyback-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Smartphone className="w-3.5 h-3.5 text-[#FF2D40]" />
            <span className="font-display font-semibold text-white text-sm">{item.brand} {item.model}</span>
          </div>
          {item.storage && <div className="text-xs text-zinc-500 mt-0.5">{item.storage}</div>}
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>
          {meta.label}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-3 text-xs text-zinc-400">
        <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {CONDITION_LABEL[item.condition] || item.condition}/5</span>
        {item.images?.length > 0 && <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {item.images.length}</span>}
      </div>

      {ai.min_price > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">KI-Schätzung</div>
          <div className="font-display font-bold text-white text-base numeric mt-0.5">€{ai.min_price}–{ai.max_price}</div>
        </div>
      )}

      {item.final_offer > 0 && (
        <div className="mt-2 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs">
          <span className="text-emerald-400 font-semibold">Angebot: €{item.final_offer}</span>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] text-zinc-500">
        <span>{item.customer_name}</span>
        <span>{(item.created_at || "").slice(0, 10)}</span>
      </div>
    </button>
  );
}

function BuybackDetailDialog({ item, onClose, onUpdated }) {
  const [status, setStatus] = useState("new");
  const [finalOffer, setFinalOffer] = useState("");
  const [validDays, setValidDays] = useState(7);
  const [adminNote, setAdminNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setStatus(item.status || "new");
      setFinalOffer(item.final_offer ? String(item.final_offer) : "");
      setValidDays(item.offer_valid_days || 7);
      setAdminNote(item.admin_note || "");
    }
  }, [item]);

  if (!item) return null;
  const meta = STATUS_META[status] || STATUS_META.new;
  const ai = item.ai_estimate || {};

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status, admin_note: adminNote };
      if (finalOffer) payload.final_offer = Number(finalOffer);
      await api.patch(`/admin/buyback/${item.id}`, payload);
      toast.success("Anfrage aktualisiert");
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const sendOffer = async () => {
    if (!finalOffer || Number(finalOffer) <= 0) {
      toast.error("Bitte gib einen Angebotspreis > 0 ein");
      return;
    }
    if (!item.email) {
      toast.error("Kunde hat keine E-Mail-Adresse — bitte telefonisch kontaktieren");
      return;
    }
    if (!confirm(`Verbindliches Angebot über € ${finalOffer} an ${item.customer_name} senden?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/buyback/${item.id}/send-offer`, {
        price: Number(finalOffer),
        valid_days: Number(validDays) || 7,
      });
      const mailInfo = data?.email_status === "sent"
        ? "Mail versendet."
        : data?.email_status === "pending_provider"
          ? "Mail wartet auf E-Mail-Provider (Resend). Kein Datenverlust."
          : `Mail-Status: ${data?.email_status}`;
      toast.success(`Angebot gespeichert · ${mailInfo}`);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Angebot konnte nicht gesendet werden");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm("Diese Ankaufsanfrage endgültig löschen?")) return;
    setSaving(true);
    try {
      await api.delete(`/admin/buyback/${item.id}`);
      toast.success("Anfrage gelöscht");
      onUpdated();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const phoneClean = (item.phone || "").replace(/[^\d+]/g, "");
  const waLink = phoneClean ? `https://wa.me/${phoneClean.replace(/^\+/, "")}` : null;

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0F0F11] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="buyback-detail-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Smartphone className="w-4 h-4 text-[#FF2D40]" />
            <DialogTitle className="font-display text-xl">{item.brand} {item.model}</DialogTitle>
            {item.storage && <span className="text-sm text-zinc-500">· {item.storage}</span>}
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${meta.cls}`}>
              {meta.label}
            </span>
          </div>
          <DialogDescription className="text-zinc-500 text-xs mt-1">
            Eingegangen am {(item.created_at || "").replace("T", " ").slice(0, 16)} · Ref {item.id.slice(0, 8).toUpperCase()}
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-5 mt-2">
          {/* Left: Customer + device details */}
          <div className="space-y-4">
            <div className="rounded-xl bg-[#0A0A0B] border border-white/5 p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Kunde</div>
              <div className="font-medium text-white" data-testid="buyback-detail-name">{item.customer_name}</div>
              <div className="flex flex-col gap-1.5 mt-2 text-sm">
                <a href={`tel:${item.phone}`} className="flex items-center gap-1.5 text-zinc-300 hover:text-white" data-testid="buyback-detail-phone">
                  <Phone className="w-3.5 h-3.5 text-[#FF2D40]" /> {item.phone}
                </a>
                {waLink && (
                  <a href={waLink} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300" data-testid="buyback-detail-whatsapp">
                    <MessageCircle className="w-3.5 h-3.5" /> WhatsApp öffnen
                  </a>
                )}
                {item.email && (
                  <a href={`mailto:${item.email}`} className="flex items-center gap-1.5 text-zinc-300 hover:text-white" data-testid="buyback-detail-email">
                    <Mail className="w-3.5 h-3.5 text-[#FF2D40]" /> {item.email}
                  </a>
                )}
              </div>
            </div>

            <div className="rounded-xl bg-[#0A0A0B] border border-white/5 p-4 space-y-2.5 text-sm">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Gerätedetails</div>
              <Row label="Zustand" value={`${CONDITION_LABEL[item.condition] || item.condition}/5 (${item.condition})`} />
              <Row label="Speicher" value={item.storage || "—"} />
              <Row label="Zubehör" value={item.accessories?.length ? item.accessories.join(", ") : "keine Angabe"} />
              {item.notes && (
                <div className="pt-2 border-t border-white/5">
                  <div className="text-xs text-zinc-500 mb-1">Anmerkungen vom Kunden:</div>
                  <p className="text-xs text-zinc-300 leading-relaxed">{item.notes}</p>
                </div>
              )}
            </div>

            {item.images?.length > 0 && (
              <div className="rounded-xl bg-[#0A0A0B] border border-white/5 p-4">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Fotos</div>
                <div className="grid grid-cols-3 gap-2">
                  {item.images.map((url, i) => (
                    <a key={i} href={url} target="_blank" rel="noreferrer" className="aspect-square rounded-lg overflow-hidden border border-white/10 bg-[#141417]" data-testid={`buyback-image-${i}`}>
                      <img src={url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: AI estimate + admin actions */}
          <div className="space-y-4">
            {ai.min_price > 0 && (
              <div className="rounded-xl bg-gradient-to-br from-[#1a0509] to-[#0A0A0B] border border-[#DC1928]/30 p-4">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#FF2D40] font-bold">
                  <Sparkles className="w-3 h-3" /> Unverbindliche KI-Schätzung
                </div>
                <div className="font-display text-3xl font-bold text-white numeric mt-1.5">€{ai.min_price}–{ai.max_price}</div>
                {ai.reasoning && <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{ai.reasoning}</p>}
                <p className="text-[10px] text-zinc-500 mt-2 italic">Nur Orientierung — kein verbindliches Angebot. Der finale Preis wird manuell festgelegt.</p>
                {ai.source && <p className="text-[10px] text-zinc-600 mt-2">Quelle: {ai.source === "ai" ? "GPT-4o-mini" : "Fallback"}</p>}
              </div>
            )}

            <div className="rounded-xl bg-[#0A0A0B] border border-white/5 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Mein Angebot</div>
                <span className="text-[10px] text-zinc-600 uppercase tracking-wider">brutto · inkl. USt</span>
              </div>

              <div>
                <Label className="text-xs text-zinc-400">Verbindlicher Angebotspreis (€)</Label>
                <Input
                  type="number"
                  value={finalOffer}
                  onChange={(e) => setFinalOffer(e.target.value)}
                  placeholder="z.B. 350"
                  className="bg-[#141417] border-white/10 text-white mt-1"
                  data-testid="buyback-final-offer"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Gültig für (Tage)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={validDays}
                    onChange={(e) => setValidDays(e.target.value)}
                    className="bg-[#141417] border-white/10 text-white mt-1"
                    data-testid="buyback-valid-days"
                  />
                </div>
                <div className="flex flex-col justify-end">
                  <Button
                    type="button"
                    onClick={sendOffer}
                    disabled={saving || !finalOffer || !item.email}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-full h-10 disabled:opacity-50"
                    data-testid="buyback-send-offer"
                    title={!item.email ? "Kunde hat keine E-Mail-Adresse" : ""}
                  >
                    <Send className="w-4 h-4 mr-1.5" /> Angebot senden
                  </Button>
                </div>
              </div>
              {item.offer_sent_at && (
                <div className="text-[11px] text-zinc-500 -mt-1">
                  Letztes Angebot: <span className="text-zinc-300 font-medium">€{item.final_offer}</span>
                  {" · "}{(item.offer_sent_at || "").replace("T", " ").slice(0, 16)}
                  {" · gültig bis "}{(item.offer_valid_until || "").replace("T", " ").slice(0, 16)}
                  {item.offer_email_status && <> · Mail: <span className="uppercase">{item.offer_email_status}</span></>}
                </div>
              )}

              <div>
                <Label className="text-xs text-zinc-400">Status manuell setzen</Label>
                <div className="grid grid-cols-3 gap-1 mt-1.5">
                  {["new", "under_review", "offer_sent", "customer_accepted", "customer_declined", "device_received", "device_checked", "completed", "cancelled"].map((s) => {
                    const m = STATUS_META[s];
                    const active = status === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setStatus(s)}
                        className={`px-2 py-1.5 rounded-md text-[10px] font-medium transition-all border ${
                          active
                            ? "bg-[#DC1928] border-[#DC1928] text-white"
                            : "bg-[#141417] border-white/10 text-zinc-400 hover:text-white"
                        }`}
                        data-testid={`buyback-status-${s}`}
                      >
                        {m?.label || s}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-xs text-zinc-400">Interne Notiz (nur Admin)</Label>
                <Textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder="z.B. Kunde will Bar-Auszahlung im Shop"
                  rows={3}
                  className="bg-[#141417] border-white/10 text-white mt-1"
                  data-testid="buyback-admin-note"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-row gap-2 sm:gap-3 mt-2">
          <Button
            variant="outline"
            onClick={remove}
            disabled={saving}
            className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 rounded-full"
            data-testid="buyback-delete"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Löschen
          </Button>
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            className="bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white rounded-full"
            data-testid="buyback-cancel"
          >
            Abbrechen
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#DC1928] hover:bg-[#B01829] text-white rounded-full"
            data-testid="buyback-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <span className="text-xs text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs text-zinc-300 text-right">{value}</span>
    </div>
  );
}
